import "./styles/main.scss";
import { mountChatWidget } from "./chat-widget";
import { mountContactForm } from "./contact-form";
import {
  detectLocale,
  getDict,
  otherLocale,
  persistLocale,
  type Dict,
  type Locale,
} from "./i18n";
import { applyTheme, detectTheme, persistTheme, toggleTheme, type Theme } from "./theme";

const app = document.getElementById("app");
if (!app) throw new Error("missing #app root");

let currentLocale: Locale = detectLocale();
let currentTheme: Theme = detectTheme();
let mobileMenuOpen = false;

applyTheme(currentTheme);
document.documentElement.lang = getDict(currentLocale).htmlLang;

function escapeAttr(s: string): string {
  return s.replace(/[&"<>]/g, (c) => ({ "&": "&amp;", '"': "&quot;", "<": "&lt;", ">": "&gt;" })[c]!);
}
function escapeText(s: string): string {
  return s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c]!);
}

function renderHeader(d: Dict): string {
  const themeLabel = currentTheme === "dark" ? d.ui.themeToggleLight : d.ui.themeToggleDark;
  return `
    <a class="skip-link" href="#main">${escapeText(d.ui.skipToContent)}</a>
    <header class="header">
      <div class="header__inner">
        <a class="header__brand" href="#">${escapeText(d.hero.name)}</a>
        <nav class="header__nav" aria-label="primary">
          <a href="#about">${escapeText(d.nav.about)}</a>
          <a href="#process">${escapeText(d.nav.process)}</a>
          <a href="#build">${escapeText(d.nav.build)}</a>
          <a href="#cases">${escapeText(d.nav.cases)}</a>
          <a href="#contacts">${escapeText(d.nav.contacts)}</a>
        </nav>
        <div class="header__actions">
          <button class="icon-btn header__theme-btn" type="button" data-action="theme" aria-label="${escapeAttr(themeLabel)}">${currentTheme === "dark" ? "Light" : "Dark"}</button>
          <button class="icon-btn header__lang-btn" type="button" data-action="lang" aria-label="${escapeAttr(d.ui.langToggle)}">${escapeText(d.ui.langToggle)}</button>
          <button class="header__hamburger" type="button" data-action="menu" aria-label="${escapeAttr(d.ui.menu)}" aria-expanded="${mobileMenuOpen ? "true" : "false"}" aria-controls="mobile-menu">
            <span></span><span></span><span></span>
          </button>
        </div>
      </div>
    </header>
  `;
}

function renderMobileMenu(d: Dict): string {
  const themeLabel = currentTheme === "dark" ? d.ui.themeToggleLight : d.ui.themeToggleDark;
  return `
    <div id="mobile-menu" class="mobile-menu ${mobileMenuOpen ? "mobile-menu--open" : ""}" aria-hidden="${mobileMenuOpen ? "false" : "true"}">
      <div class="mobile-menu__backdrop" data-action="menu-close"></div>
      <aside class="mobile-menu__panel" role="dialog" aria-modal="true" aria-label="${escapeAttr(d.ui.menu)}">
        <div class="mobile-menu__head">
          <span class="mobile-menu__title">${escapeText(d.hero.name)}</span>
          <button class="mobile-menu__close icon-btn" type="button" data-action="menu-close" aria-label="${escapeAttr(d.ui.close)}">×</button>
        </div>
        <nav class="mobile-menu__nav" aria-label="mobile">
          <a href="#about" data-menu-link>${escapeText(d.nav.about)}</a>
          <a href="#process" data-menu-link>${escapeText(d.nav.process)}</a>
          <a href="#build" data-menu-link>${escapeText(d.nav.build)}</a>
          <a href="#cases" data-menu-link>${escapeText(d.nav.cases)}</a>
          <a href="#contacts" data-menu-link>${escapeText(d.nav.contacts)}</a>
        </nav>
        <div class="mobile-menu__switchers">
          <button class="icon-btn icon-btn--wide" type="button" data-action="theme" aria-label="${escapeAttr(themeLabel)}">${escapeText(themeLabel)}</button>
          <button class="icon-btn icon-btn--wide" type="button" data-action="lang" aria-label="${escapeAttr(d.ui.langToggle)}">${escapeText(d.ui.langSwitchLabel)}</button>
        </div>
      </aside>
    </div>
  `;
}

function renderHero(d: Dict): string {
  return `
    <header class="hero">
      <div class="container">
        <p class="hero__eyebrow">${escapeText(d.hero.eyebrow)}</p>
        <h1 class="hero__name">${escapeText(d.hero.name)}</h1>
        <p class="hero__line">${escapeText(d.hero.tagline)}</p>
      </div>
    </header>
  `;
}

function renderAbout(d: Dict): string {
  return `
    <section id="about" class="section">
      <div class="container">
        <h2 class="section__title">${escapeText(d.about.title)}</h2>
        <div class="grid grid--3">
          <article class="card">
            <h3>${escapeText(d.about.stack.heading)}</h3>
            <ul class="tags">
              ${d.about.stack.items.map((s) => `<li>${escapeText(s)}</li>`).join("")}
            </ul>
          </article>
          <article class="card">
            <h3>${escapeText(d.about.experience.heading)}</h3>
            <p>${escapeText(d.about.experience.body)}</p>
          </article>
          <article class="card">
            <h3>${escapeText(d.about.directions.heading)}</h3>
            <ul class="bullets">
              ${d.about.directions.items.map((s) => `<li>${escapeText(s)}</li>`).join("")}
            </ul>
          </article>
        </div>
      </div>
    </section>
  `;
}

function renderProcess(d: Dict): string {
  return `
    <section id="process" class="section section--alt">
      <div class="container">
        <h2 class="section__title">${escapeText(d.process.title)}</h2>
        <div class="grid grid--2">
          <article class="card">
            <h3>${escapeText(d.process.approach.heading)}</h3>
            <ol class="bullets bullets--ordered">
              ${d.process.approach.steps.map((s) => `<li>${escapeText(s)}</li>`).join("")}
            </ol>
          </article>
          <article class="card">
            <h3>${escapeText(d.process.ai.heading)}</h3>
            <ul class="bullets">
              ${d.process.ai.items.map((s) => `<li>${escapeText(s)}</li>`).join("")}
            </ul>
          </article>
        </div>
      </div>
    </section>
  `;
}

function renderBuild(d: Dict): string {
  return `
    <section id="build" class="section">
      <div class="container">
        <h2 class="section__title">${escapeText(d.build.title)}</h2>
        <p class="section__intro">${escapeText(d.build.intro)}</p>
        <div class="grid grid--2">
          ${d.build.items
            .map(
              (it) => `
            <article class="card">
              <h3>${escapeText(it.heading)}</h3>
              <p>${escapeText(it.body)}</p>
            </article>
          `,
            )
            .join("")}
        </div>
      </div>
    </section>
  `;
}

function renderCases(d: Dict): string {
  return `
    <section id="cases" class="section">
      <div class="container">
        <h2 class="section__title">${escapeText(d.cases.title)}</h2>
        <div class="grid grid--2">
          ${d.cases.items
            .map(
              (it) => `
            <article class="card card--link">
              <h3>${escapeText(it.heading)}</h3>
              <p class="card__sub">${escapeText(it.sub)}</p>
              <p>${escapeText(it.body)}</p>
              ${(it.links || [])
                .map((l) => `<a class="link" href="${escapeAttr(l.href)}" target="_blank" rel="noopener">${escapeText(l.label)}</a>`)
                .join("")}
            </article>
          `,
            )
            .join("")}
        </div>
      </div>
    </section>
  `;
}

function renderContacts(d: Dict): string {
  const linkAttrs = (href: string) => (href.startsWith("mailto:") ? "" : ' target="_blank" rel="noopener"');
  return `
    <section id="contacts" class="section section--alt">
      <div class="container">
        <h2 class="section__title">${escapeText(d.contacts.title)}</h2>
        <div class="contacts">
          ${d.contacts.links
            .map((l) => `<a class="link" href="${escapeAttr(l.href)}"${linkAttrs(l.href)}>${escapeText(l.label)}</a>`)
            .join("")}
        </div>
        <div class="form-wrap">
          <h3 class="form-wrap__title">${escapeText(d.contacts.form.title)}</h3>
          <p class="form-wrap__sub">${escapeText(d.contacts.form.sub)}</p>
          <div id="contact-form"></div>
        </div>
      </div>
    </section>
  `;
}

function renderFooter(d: Dict): string {
  return `
    <footer class="footer">
      <div class="container footer__inner">
        <p class="footer__text">${escapeText(d.footer.text)}</p>
        <a class="footer__link" href="${escapeAttr(d.footer.repoHref)}" target="_blank" rel="noopener">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.91.58.11.79-.25.79-.56v-2.01c-3.2.7-3.87-1.37-3.87-1.37-.52-1.33-1.27-1.69-1.27-1.69-1.04-.71.08-.7.08-.7 1.15.08 1.76 1.18 1.76 1.18 1.02 1.75 2.68 1.25 3.34.95.1-.74.4-1.25.73-1.54-2.55-.29-5.24-1.28-5.24-5.7 0-1.26.45-2.29 1.19-3.1-.12-.29-.52-1.46.11-3.04 0 0 .97-.31 3.18 1.18a11 11 0 0 1 5.79 0c2.21-1.49 3.17-1.18 3.17-1.18.63 1.58.24 2.75.12 3.04.74.81 1.19 1.84 1.19 3.1 0 4.43-2.69 5.41-5.26 5.69.41.35.78 1.05.78 2.12v3.14c0 .31.21.68.8.56C20.21 21.39 23.5 17.08 23.5 12 23.5 5.65 18.35.5 12 .5Z"/></svg>
          <span>${escapeText(d.footer.sourceLabel)}</span>
        </a>
      </div>
    </footer>
  `;
}

function mount() {
  const d = getDict(currentLocale);
  document.documentElement.lang = d.htmlLang;
  document.title = d.meta.title;
  document.querySelector('meta[name="description"]')?.setAttribute("content", d.meta.description);

  app!.innerHTML =
    renderHeader(d) +
    renderMobileMenu(d) +
    `<main id="main">` +
    renderHero(d) +
    renderAbout(d) +
    renderProcess(d) +
    renderBuild(d) +
    renderCases(d) +
    renderContacts(d) +
    `</main>` +
    renderFooter(d) +
    `<div id="chat-widget"></div>`;

  document.body.classList.toggle("body--menu-open", mobileMenuOpen);

  mountContactForm(document.getElementById("contact-form")!, d, currentLocale);
  mountChatWidget(document.getElementById("chat-widget")!, d, currentLocale);

  app!.querySelectorAll<HTMLButtonElement | HTMLElement>('[data-action]').forEach((el) => {
    el.addEventListener("click", () => {
      const action = (el as HTMLElement).dataset.action;
      if (action === "theme") {
        currentTheme = toggleTheme(currentTheme);
        applyTheme(currentTheme);
        persistTheme(currentTheme);
        mount();
      } else if (action === "lang") {
        currentLocale = otherLocale(currentLocale);
        persistLocale(currentLocale);
        mount();
      } else if (action === "menu") {
        mobileMenuOpen = !mobileMenuOpen;
        mount();
      } else if (action === "menu-close") {
        mobileMenuOpen = false;
        mount();
      }
    });
  });

  app!.querySelectorAll<HTMLAnchorElement>("[data-menu-link]").forEach((a) => {
    a.addEventListener("click", () => {
      mobileMenuOpen = false;
      // Re-mount after the browser handles the hash navigation so layout reflects state.
      setTimeout(() => mount(), 0);
    });
  });
}

document.addEventListener("keydown", (e) => {
  if (e.key === "Escape" && mobileMenuOpen) {
    mobileMenuOpen = false;
    mount();
  }
});

mount();
